import { observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { RootStoreContext } from '../../stores/RootStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import LevelProgressCard from './progress/LevelProgressCard';
import AttendanceCharts from './progress/AttendanceCharts';
import './MyProgressStep.css';
import GamifiedLevelHeader from './progress/GamifiedLevelHeader';
import StreakRewardsList from './progress/StreakRewardsList';

const MyProgressStep = observer(() => {
  const { registrationStore } = useContext(RootStoreContext);
  const { studentProgress, clearSelectedItems, toSellingItems, loadStudentProgress } = registrationStore;
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const currentLevels = studentProgress?.levelProgressList.filter(lp => lp.promotion.nextLevelName) || [];
    if (studentProgress && currentLevels.length > 1) {
      const timer = setInterval(() => {
        setIndex((prev) => (prev + 1) % currentLevels.length);
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [studentProgress, index]);

  useEffect(() => {
    if (!studentProgress) {
      loadStudentProgress();
    }
  }, [studentProgress, loadStudentProgress]);

  const handleHomeClick = () => {
    clearSelectedItems();
    toSellingItems();
  };

  if (!studentProgress) {
    return (
      <Container>
        <div className="text-center mt-4">Loading progress...</div>
      </Container>
    );
  }

  return (
    <Container className="my-progress-container">
      <div className="progress-page-header">
        <button className="secondary-nav-button" onClick={handleHomeClick}>
          <FontAwesomeIcon icon={faChevronLeft} />
          <span>Back</span>
        </button>
      </div>

      <h2 className="progress-title">My current level</h2>

      {/* Gamified Header Custom Carousel - Current levels only */}
      {studentProgress.levelProgressList.filter(lp => lp.promotion.nextLevelName).length > 0 && (
        <div className="level-carousel-container mb-4">
          <div className="level-carousel-wrapper">
            {studentProgress.levelProgressList
              .slice()
              .filter(lp => lp.promotion.nextLevelName)
              .sort((a, b) => b.promotion.levelNumber - a.promotion.levelNumber)
              .map((levelProgress, idx) => (
                <div
                  key={`level-carousel-item-${levelProgress.promotion.assignmentId}-${idx}`}
                  className={`level-carousel-item ${idx === index ? 'active' : ''}`}
                >
                  <GamifiedLevelHeader
                    levelProgress={levelProgress}
                    attendanceStats={studentProgress.attendanceStats}
                  />
                </div>
              ))}
          </div>

          {studentProgress.levelProgressList.filter(lp => lp.promotion.nextLevelName).length > 1 && (
            <>
              <button
                className="level-carousel-control level-carousel-control-prev"
                onClick={() => setIndex((prev) => (prev - 1 + studentProgress.levelProgressList.filter(lp => lp.promotion.nextLevelName).length) % studentProgress.levelProgressList.filter(lp => lp.promotion.nextLevelName).length)}
                aria-label="Previous"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button
                className="level-carousel-control level-carousel-control-next"
                onClick={() => setIndex((prev) => (prev + 1) % studentProgress.levelProgressList.filter(lp => lp.promotion.nextLevelName).length)}
                aria-label="Next"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </>
          )}
        </div>
      )}

      {/* Current level tiles - under "My current level" section */}
      {studentProgress.levelProgressList.length > 0 && (
        <div className="level-progress-list mb-4">
          {studentProgress.levelProgressList
            .slice()
            .filter(lp => lp.promotion.nextLevelName)
            .sort((a, b) => b.promotion.levelNumber - a.promotion.levelNumber)
            .map((levelProgress, index) => (
              <LevelProgressCard
                key={`current-level-card-${levelProgress.promotion.assignmentId}-${index}`}
                levelProgress={levelProgress}
                displayMode="current"
              />
            ))}
        </div>
      )}

      <section className="progress-rewards-section">
        <h3 className="levels-section-title">My Streak rewards</h3>
        <StreakRewardsList
          rewards={(studentProgress.attendanceStats?.streakRewards || []).filter(r => r.status !== 'expired')}
        />
      </section>

      {/* My Progress History */}
      <h3 className="levels-section-title">My progress history</h3>
      {studentProgress.levelProgressList.length === 0 ? (
        <div className="no-progress-message">
          No level assignments yet. Keep attending classes to unlock new levels!
        </div>
      ) : (
        <div className="level-progress-list">
          {/* All levels shown as history */}
          {studentProgress.levelProgressList
            .slice()
            .sort((a, b) => b.promotion.levelNumber - a.promotion.levelNumber)
            .map((levelProgress, index) => (
              <LevelProgressCard
                key={`graduated-level-card-${levelProgress.promotion.assignmentId}-${index}`}
                levelProgress={levelProgress}
                displayMode="graduated"
              />
            ))}
        </div>
      )}
    </Container>
  );
});

export default MyProgressStep;
